/** Dialoge zu Nachrichten: Löschen bestätigen, Thread aus Nachricht erstellen. */
import { FIXED_LIMITS } from '@lagerfeuer/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ConfirmDialog, Modal } from '../../components/Modal';
import { errorMessage } from '../../lib/api';
import { threadsApi, type AutoArchive } from '../../lib/endpoints';
import { pathForChannel } from '../../lib/routes';
import { appState } from '../../store/app';
import { deleteMessage, useMessageDialog } from './actions';
import { Markdown } from './Markdown';

function DeleteDialog() {
  const dialog = useMessageDialog((s) => s.dialog);
  const close = useMessageDialog((s) => s.close);
  const [busy, setBusy] = useState(false);
  if (dialog?.kind !== 'delete') return null;
  const m = dialog.message;
  return (
    <ConfirmDialog
      title="Nachricht löschen"
      message="Willst du diese Nachricht wirklich löschen? Tipp: Mit gedrückter Umschalttaste geht es ohne Nachfrage."
      confirmLabel="Löschen"
      danger
      busy={busy}
      onClose={close}
      onConfirm={() => {
        setBusy(true);
        void deleteMessage(m).finally(() => {
          setBusy(false);
          close();
        });
      }}
    >
      <div className="msg-quote" data-testid="delete-preview">
        <Markdown content={m.content || (m.attachments.length ? `${m.attachments.length} Anhang/Anhänge` : '')} serverId={m.serverId} inline />
      </div>
    </ConfirmDialog>
  );
}

function ThreadDialog() {
  const dialog = useMessageDialog((s) => s.dialog);
  const close = useMessageDialog((s) => s.close);
  const navigate = useNavigate();
  const initial = dialog?.kind === 'thread' ? defaultThreadName(dialog.message.content) : '';
  const [name, setName] = useState(initial);
  const [archive, setArchive] = useState<AutoArchive>(1440);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (dialog?.kind !== 'thread') return null;
  const m = dialog.message;
  const submit = async () => {
    const n = name.trim();
    if (!n) return;
    setBusy(true);
    setError(null);
    try {
      const ch = await threadsApi.fromMessage(m.channelId, m.id, { name: n.slice(0, FIXED_LIMITS.threadTitleMax), autoArchiveMinutes: archive });
      appState().dispatch('THREAD_CREATE', ch);
      close();
      void navigate(pathForChannel(ch));
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title="Thread erstellen"
      onClose={close}
      testId="thread-create-dialog"
      footer={
        <>
          <button className="btn btn-ghost" onClick={close}>
            Abbrechen
          </button>
          <button className="btn btn-primary" disabled={busy || !name.trim()} onClick={() => void submit()} data-testid="thread-create-submit">
            Erstellen
          </button>
        </>
      }
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div className="field">
          <label htmlFor="thread-name">Name des Threads</label>
          <input
            id="thread-name"
            className="input"
            autoFocus
            value={name}
            maxLength={FIXED_LIMITS.threadTitleMax}
            onChange={(e) => setName(e.target.value)}
            data-testid="thread-name-input"
          />
        </div>
        <div className="field">
          <label htmlFor="thread-archive">Automatisch archivieren nach</label>
          <select id="thread-archive" className="select" value={archive} onChange={(e) => setArchive(Number(e.target.value) as AutoArchive)} data-testid="thread-archive-select">
            <option value={60}>1 Stunde</option>
            <option value={1440}>24 Stunden</option>
            <option value={4320}>3 Tagen</option>
            <option value={10080}>1 Woche</option>
          </select>
        </div>
        {error && <p className="error-text" role="alert">{error}</p>}
      </form>
    </Modal>
  );
}

export function defaultThreadName(content: string): string {
  const plain = content
    .replace(/<a?:([A-Za-z0-9_]+):\d+>/g, ':$1:')
    .replace(/<[@#][!&]?\d+>/g, '')
    .replace(/[*_~`|>#-]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return (plain || 'Neuer Thread').slice(0, 40);
}

/** Je Kanal genau einmal gerendert (Dialoge betreffen nur Nachrichten dieses Kanals). */
export function MessageDialogs({ channelId }: { channelId: string }) {
  const kind = useMessageDialog((s) => (s.dialog?.message.channelId === channelId ? s.dialog.kind : undefined));
  const id = useMessageDialog((s) => s.dialog?.message.id);
  if (kind === 'delete') return <DeleteDialog />;
  if (kind === 'thread') return <ThreadDialog key={id} />;
  return null;
}
